import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, Award, Trash2, Plus, Edit2, ChevronUp, ChevronDown, MoreVertical, Save, RotateCcw, ChevronRight } from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { projectAPI, createQueryKey } from '../services/api';
import toast from 'react-hot-toast';

/**
 * Reusable ObjectivesList component - Compact Design
 * High-density list with progressive disclosure
 * 
 * @param {Object} props
 * @param {Object} props.project - The project object containing objectives (used in 'live' mode)
 * @param {Object} props.currentUser - The current logged-in user
 * @param {boolean} props.showHeader - Whether to show the header with icon and progress bar (default: true)
 * @param {boolean} props.showAddButton - Whether to show the add objective button for owners (default: true)
 * @param {boolean} props.compact - Use compact styling (default: false) - now default behavior
 * @param {string} props.className - Additional CSS classes for the container
 * @param {string} props.mode - 'live' (API mutations) or 'local' (form state management) (default: 'live')
 * @param {Array} props.objectives - Array of objectives (used in 'local' mode)
 * @param {Function} props.onChange - Callback when objectives change (used in 'local' mode)
 * @param {boolean} props.showCompletionFeatures - Whether to show completion toggle (default: true in live mode, false in local mode)
 */
const ObjectivesList = ({ 
  project, 
  currentUser, 
  showHeader = true, 
  showAddButton = true,
  compact = false,
  className = '',
  mode = 'live',
  objectives: localObjectives,
  onChange,
  showCompletionFeatures
}) => {
  const isLocalMode = mode === 'local';
  const shouldShowCompletion = showCompletionFeatures !== undefined 
    ? showCompletionFeatures 
    : !isLocalMode;
    
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newObjectiveName, setNewObjectiveName] = useState('');
  const [newObjectiveDescription, setNewObjectiveDescription] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const menuRefs = useRef({});
  const menuButtonRefs = useRef({});
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuOpenId) {
        const menuPortal = document.getElementById('objectives-menu-portal');
        const buttonRef = menuButtonRefs.current[menuOpenId];
        const clickedInMenu = menuPortal && menuPortal.contains(event.target);
        const clickedOnButton = buttonRef && buttonRef.contains(event.target);
        if (!clickedInMenu && !clickedOnButton) {
          setMenuOpenId(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpenId]);

  const openMenu = (objectiveKey) => {
    const buttonEl = menuButtonRefs.current[objectiveKey];
    if (buttonEl) {
      const rect = buttonEl.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      });
    }
    setMenuOpenId(objectiveKey);
  };

  const canEditObjectives = React.useMemo(() => {
    if (isLocalMode) return true;
    if (!currentUser || !project) return false;
    const viewerId = currentUser.id;
    if (!viewerId) return false;
    if (project.ownerId === viewerId || project.owner?.id === viewerId) return true;
    if (currentUser.role === 'admin') return true;
    const collaboratorList = Array.isArray(project.collaborators) ? project.collaborators : [];
    const participantList = Array.isArray(project.participants) ? project.participants : [];
    const isCollaborator = collaboratorList.some((collab) => {
      const collabUserId = collab?.userId || collab?.user?.id || collab?.id;
      return collabUserId && collabUserId === viewerId;
    });
    if (isCollaborator) return true;
    return participantList.some((participant) => {
      const participantId = participant?.userId || participant?.id;
      return participantId && participantId === viewerId;
    });
  }, [isLocalMode, currentUser, project]);
  
  const objectives = isLocalMode ? (localObjectives || []) : (project?.objectives || []);
  const sortedObjectives = [...objectives].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  const completedCount = sortedObjectives.filter(o => o.isCompleted === true).length;
  const totalCount = sortedObjectives.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const { data: participantsResp } = useQuery({
    queryKey: ['participants', project?.id],
    queryFn: () => project?.id ? projectAPI.getParticipants(project.id) : Promise.resolve({ data: { data: { participants: [] } } }),
    enabled: !!project?.id,
    staleTime: 5 * 60 * 1000
  });

  const userMap = React.useMemo(() => {
    const map = new Map();
    if (project?.owner) {
      const ownerId = String(project.owner.id || project.ownerId || '');
      if (ownerId) map.set(ownerId, project.owner);
    }
    const participants = participantsResp?.data?.data?.participants || [];
    participants.forEach(p => {
      const pid = String(p?.id || '');
      if (pid) map.set(pid, p);
    });
    return map;
  }, [project?.owner, project?.ownerId, participantsResp]);

  const formatCompletionDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return null;
    }
  };

  const getUserDisplayName = (userId) => {
    if (!userId) return null;
    const user = userMap.get(String(userId));
    if (!user) return null;
    if (user.username) return `@${user.username}`;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.email) return user.email.split('@')[0];
    return null;
  };

  const addObjectiveLocal = (data) => {
    const maxOrderIndex = sortedObjectives.length > 0 
      ? Math.max(...sortedObjectives.map(o => o.orderIndex || 0))
      : -1;
    const newObjective = {
      _localKey: `new-${Date.now()}`,
      objectiveName: data.name,
      objectiveDescription: data.description || null,
      objectiveExtraData: {},
      objectiveCreatedByUserId: currentUser?.id || null,
      objectiveIsPredefined: false,
      objectiveCreatedAt: new Date().toISOString(),
      objectiveUpdatedAt: new Date().toISOString(),
      orderIndex: maxOrderIndex + 1,
      targetCount: 1,
      isOpenEnded: false,
      isCompleted: false,
      notes: ''
    };
    onChange([...objectives, newObjective]);
    setShowAddForm(false);
    setNewObjectiveName('');
    setNewObjectiveDescription('');
  };

  const addObjectiveMutation = useMutation({
    mutationFn: async (data) => {
      if (!project?.id) throw new Error('Project ID is required');
      const maxOrderIndex = sortedObjectives.length > 0 
        ? Math.max(...sortedObjectives.map(o => o.orderIndex || 0))
        : -1;
      return projectAPI.addObjective(project.id, {
        objectiveName: data.name,
        objectiveDescription: data.description || null,
        orderIndex: maxOrderIndex + 1,
        targetCount: 1,
        isOpenEnded: false
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.project(project.id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setShowAddForm(false);
      setNewObjectiveName('');
      setNewObjectiveDescription('');
    },
    onError: () => toast.error('Failed to add objective')
  });

  const getObjectiveKey = (obj) => obj.id || obj._localKey || obj.orderIndex;

  const updateObjectiveLocal = ({ objectiveId, updates }) => {
    const updated = objectives.map(obj => 
      getObjectiveKey(obj) === objectiveId ? { ...obj, ...updates } : obj
    );
    onChange(updated);
    cancelEdit();
  };

  const updateObjectiveMutation = useMutation({
    mutationFn: async ({ objectiveId, updates }) => {
      if (!project?.id) throw new Error('Project ID is required');
      return projectAPI.batchUpdateObjectives(project.id, [{
        action: 'update',
        objective_id: objectiveId,
        updates: updates
      }]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.project(project.id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      cancelEdit();
    },
    onError: () => toast.error('Failed to update objective')
  });

  const deleteObjectiveLocal = (objectiveId, objectiveName) => {
    const updated = objectives
      .filter(obj => getObjectiveKey(obj) !== objectiveId)
      .map((obj, index) => ({ ...obj, orderIndex: index }));
    onChange(updated);
    setMenuOpenId(null);
  };

  const deleteObjectiveMutation = useMutation({
    mutationFn: async (objectiveId) => {
      if (!project?.id) throw new Error('Project ID is required');
      return projectAPI.removeObjective(project.id, objectiveId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.project(project.id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setMenuOpenId(null);
    },
    onError: () => toast.error('Failed to remove objective')
  });

  const deleteAllObjectivesLocal = () => {
    onChange([]);
    setMenuOpenId(null);
    setExpandedId(null);
    setShowAddForm(false);
  };

  const deleteAllObjectivesMutation = useMutation({
    mutationFn: async (objectiveIds) => {
      if (!project?.id) throw new Error('Project ID is required');
      if (!Array.isArray(objectiveIds) || objectiveIds.length === 0) {
        return Promise.resolve();
      }
      const payload = objectiveIds.map((objectiveId) => ({
        action: 'delete',
        objective_id: objectiveId
      }));
      return projectAPI.batchUpdateObjectives(project.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.project(project.id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setMenuOpenId(null);
      setExpandedId(null);
      setShowAddForm(false);
      toast.success('All objectives removed');
    },
    onError: () => toast.error('Failed to remove objectives')
  });

  const reorderObjectivesLocal = (orderedKeys) => {
    const reordered = orderedKeys.map((key, index) => {
      const obj = objectives.find(o => getObjectiveKey(o) === key);
      return obj ? { ...obj, orderIndex: index } : null;
    }).filter(Boolean);
    onChange(reordered);
  };
  
  const reorderObjectivesMutation = useMutation({
    mutationFn: async (orderedIds) => {
      if (!project?.id) throw new Error('Project ID is required');
      return projectAPI.batchUpdateObjectives(project.id, [{
        action: 'reorder',
        ordered_ids: orderedIds
      }]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.project(project.id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: () => toast.error('Failed to reorder objectives')
  });

  const toggleCompletionLocal = ({ objectiveId, isCompleted }) => {
    const updated = objectives.map(obj => 
      getObjectiveKey(obj) === objectiveId ? { ...obj, isCompleted } : obj
    );
    onChange(updated);
    setMenuOpenId(null);
  };

  const toggleCompletionMutation = useMutation({
    mutationFn: async ({ objectiveId, isCompleted }) => {
      if (!project?.id) throw new Error('Project ID is required');
      return projectAPI.batchUpdateObjectives(project.id, [{
        action: 'update',
        objective_id: objectiveId,
        updates: { isCompleted }
      }]);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: createQueryKey.project(project.id) });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setMenuOpenId(null);
    },
    onError: () => toast.error('Failed to update objective')
  });

  const handleAddObjective = () => {
    if (!newObjectiveName.trim()) {
      toast.error('Name required');
      return;
    }
    const data = { name: newObjectiveName.trim(), description: newObjectiveDescription.trim() || null };
    if (isLocalMode) addObjectiveLocal(data);
    else addObjectiveMutation.mutate(data);
  };

  const startEdit = (objective) => {
    setEditingId(getObjectiveKey(objective));
    setEditingName(objective.objectiveName || '');
    setEditingDescription(objective.objectiveDescription || '');
    setEditingNotes(objective.notes || '');
    setMenuOpenId(null);
    setExpandedId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingDescription('');
    setEditingNotes('');
    setShowAddForm(false);
    setNewObjectiveName('');
    setNewObjectiveDescription('');
  };

  const saveEdit = () => {
    if (!editingName.trim()) {
      toast.error('Name required');
      return;
    }
    const updates = {
      objectiveName: editingName.trim(),
      objectiveDescription: editingDescription.trim() || null,
      notes: editingNotes.trim() || null
    };
    if (isLocalMode) updateObjectiveLocal({ objectiveId: editingId, updates });
    else updateObjectiveMutation.mutate({ objectiveId: editingId, updates });
  };

  const handleDelete = (objectiveId, objectiveName) => {
    if (window.confirm(`Remove "${objectiveName}"?`)) {
      if (isLocalMode) deleteObjectiveLocal(objectiveId, objectiveName);
      else deleteObjectiveMutation.mutate(objectiveId);
    }
  };

  const handleToggleCompletion = (objectiveId, isCompleted) => {
    if (isLocalMode) toggleCompletionLocal({ objectiveId, isCompleted });
    else toggleCompletionMutation.mutate({ objectiveId, isCompleted });
  };

  const handleRemoveAllObjectives = () => {
    if (sortedObjectives.length === 0) return;
    const confirmMessage = sortedObjectives.length === 1
      ? 'Remove the only objective?'
      : `Remove all ${sortedObjectives.length} objectives?`;
    if (!window.confirm(confirmMessage)) return;

    setShowAddForm(false);
    setExpandedId(null);
    setMenuOpenId(null);

    if (isLocalMode) {
      deleteAllObjectivesLocal();
      toast.success('All objectives removed');
    } else {
      const objectiveIds = sortedObjectives.map(obj => obj.id).filter(Boolean);
      deleteAllObjectivesMutation.mutate(objectiveIds);
    }
  };

  const moveObjective = (index, direction) => {
    if (index === 0 && direction === 'up') return;
    if (index === sortedObjectives.length - 1 && direction === 'down') return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newOrder = [...sortedObjectives];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    const orderedKeys = newOrder.map(o => getObjectiveKey(o));
    if (isLocalMode) reorderObjectivesLocal(orderedKeys);
    else reorderObjectivesMutation.mutate(newOrder.map(o => o.id));
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && (editingId || showAddForm)) cancelEdit();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editingId, showAddForm]);

  if (!isLocalMode && totalCount === 0 && !canEditObjectives) return null;

  const hasDetails = (obj) => obj.objectiveDescription || obj.notes || 
    (shouldShowCompletion && obj.isCompleted && (obj.completedByUserId || obj.completedAt));

  return (
    <div className={className}>
      {/* Compact Header */}
      {showHeader && (
        <div className={`flex items-center justify-between gap-3 ${(sortedObjectives.length > 0 || showAddForm) ? 'mb-4' : ''}`}>
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white whitespace-nowrap">
              Objectives
            </h3>
            {totalCount > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 rounded-full ${
                      progressPercent === 100 
                        ? 'bg-green-500' 
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                  {completedCount}/{totalCount}
                </span>
              </div>
            )}
          </div>
          
          {canEditObjectives && showAddButton && !showAddForm && (
            <button 
              onClick={() => setShowAddForm(true)}
              className="flex items-center justify-center gap-1.5 pl-2 pr-2.5 py-1 rounded-lg font-semibold text-sm transition-all bg-white text-gray-700 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add</span>
            </button>
          )}
        </div>
      )}

      {/* Standalone Add Button when header is hidden */}
      {!showHeader && canEditObjectives && showAddButton && !showAddForm && (
        <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:gap-3">
          <button 
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-3 py-2 w-full text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-dashed border-blue-300 dark:border-blue-700 rounded-lg transition-colors justify-center sm:flex-1"
          >
            <Plus className="w-4 h-4" />
            <span>Add Objective</span>
          </button>
          {sortedObjectives.length > 0 && (
            <button
              type="button"
              onClick={handleRemoveAllObjectives}
              disabled={deleteAllObjectivesMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 w-full text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-dashed border-red-200 dark:border-red-800/50 rounded-lg transition-colors justify-center disabled:opacity-50 disabled:cursor-not-allowed sm:flex-1"
            >
              {deleteAllObjectivesMutation.isPending ? (
                <span>Removing...</span>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  <span>Remove All</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Inline Add Form */}
      {canEditObjectives && showAddButton && showAddForm && (
        <div className="mb-3 p-3 border border-blue-200 dark:border-blue-800/50 rounded-lg bg-blue-50/50 dark:bg-blue-900/10">
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Objective name"
              value={newObjectiveName}
              onChange={(e) => setNewObjectiveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddObjective(); }
                if (e.key === 'Escape') cancelEdit();
              }}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
              autoFocus
            />
          </div>
          <textarea
            placeholder="Description (optional)"
            value={newObjectiveDescription}
            onChange={(e) => setNewObjectiveDescription(e.target.value)}
            rows={1}
            className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none mb-2"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddObjective}
              disabled={(isLocalMode ? false : addObjectiveMutation.isPending) || !newObjectiveName.trim()}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
            >
              {!isLocalMode && addObjectiveMutation.isPending ? 'Adding...' : 'Add'}
            </button>
            <button onClick={cancelEdit} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {sortedObjectives.length === 0 && !showAddForm && (
        <div className="py-6 px-4 mt-4 text-center border border-dashed border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50/50 dark:bg-gray-800/20 flex gap-2 justify-center items-center">
          <div className="text-gray-400 dark:text-gray-500 mb-1">
            <svg className="w-5 h-5 mx-auto opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {canEditObjectives ? 'No objectives yet' : 'No objectives defined'}
          </p>
        </div>
      )}

      {/* Compact Objectives List */}
      {sortedObjectives.length > 0 && (
        <div className="border border-gray-100 dark:border-gray-700/50 rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/50">
          {sortedObjectives.map((projectObjective, index) => {
            const objectiveKey = getObjectiveKey(projectObjective);
            const isCompleted = projectObjective.isCompleted === true;
            const isEditing = editingId === objectiveKey;
            const showMenu = menuOpenId === objectiveKey;
            const isExpanded = expandedId === objectiveKey;
            const hasExtra = hasDetails(projectObjective);
              
            return (
              <div 
                key={objectiveKey} 
                className={`group transition-colors ${
                  isCompleted 
                    ? 'bg-green-50/50 dark:bg-green-900/5'
                    : 'bg-white dark:bg-gray-800/30 hover:bg-gray-50/50 dark:hover:bg-gray-700/20'
                }`}
              >
                {isEditing ? (
                  /* Compact Edit Mode */
                  <div className="p-3 space-y-2">
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                        if (e.key === 'Escape') cancelEdit();
                      }}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium"
                      autoFocus
                      placeholder="Name"
                    />
                    <textarea
                      value={editingDescription}
                      onChange={(e) => setEditingDescription(e.target.value)}
                      placeholder="Description (optional)"
                      rows={2}
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                    />
                    {isLocalMode && (
                      <textarea
                        value={editingNotes}
                        onChange={(e) => setEditingNotes(e.target.value)}
                        placeholder="Notes (optional)"
                        rows={2}
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                      />
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={(isLocalMode ? false : updateObjectiveMutation.isPending) || !editingName.trim()}
                        className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        <Save className="w-3 h-3" />
                        Save
                      </button>
                      <button onClick={cancelEdit} className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Compact View Mode */
                  <div className="flex items-center gap-2 px-3 py-2">
                    {/* Direct-click checkbox */}
                    {shouldShowCompletion ? (
                      <button
                        onClick={() => handleToggleCompletion(objectiveKey, !isCompleted)}
                        disabled={isLocalMode ? false : toggleCompletionMutation.isPending}
                        className={`flex-shrink-0 w-4.5 h-4.5 rounded border-2 flex items-center justify-center transition-all ${
                          isCompleted 
                            ? 'bg-green-500 border-green-500 text-white' 
                            : 'border-gray-300 dark:border-gray-500 hover:border-green-400 dark:hover:border-green-500'
                        }`}
                        style={{ width: '18px', height: '18px' }}
                        aria-label={isCompleted ? 'Mark incomplete' : 'Mark complete'}
                      >
                        {isCompleted && <Check className="w-3 h-3" strokeWidth={3} />}
                      </button>
                    ) : (
                      <div className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'}`} />
                    )}
                    
                    {/* Content - clickable to expand if has details */}
                    <div 
                      className={`flex-1 min-w-0 ${hasExtra ? 'cursor-pointer' : ''}`}
                      onClick={() => hasExtra && setExpandedId(isExpanded ? null : objectiveKey)}
                    >
                      <div className="flex items-center gap-2">
                        {/* Consistent visual marker: chevron for expandable, dot for non-expandable */}
                        {hasExtra ? (
                          <ChevronRight className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        ) : (
                          <span className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                            <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                          </span>
                        )}
                        <span className={`text-sm leading-tight truncate ${
                          isCompleted
                            ? 'line-through text-gray-400 dark:text-gray-500'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {projectObjective.objectiveName}
                        </span>
                        {/* Inline completion badge */}
                        {shouldShowCompletion && isCompleted && projectObjective.completedAt && !isExpanded && (
                          <span className="flex-shrink-0 text-[10px] text-green-600 dark:text-green-400 font-medium">
                            {formatCompletionDate(projectObjective.completedAt)}
                          </span>
                        )}
                      </div>
                      
                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="mt-1.5 ml-5 space-y-1">
                          {projectObjective.objectiveDescription && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                              {projectObjective.objectiveDescription}
                            </p>
                          )}
                          {projectObjective.notes && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                              {projectObjective.notes}
                            </p>
                          )}
                          {shouldShowCompletion && isCompleted && (projectObjective.completedByUserId || projectObjective.completedAt) && (
                            <div className="flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400">
                              <Award className="w-2.5 h-2.5" />
                              {projectObjective.completedByUserId && getUserDisplayName(projectObjective.completedByUserId) && (
                                <span>{getUserDisplayName(projectObjective.completedByUserId)}</span>
                              )}
                              {projectObjective.completedAt && (
                                <>
                                  {projectObjective.completedByUserId && getUserDisplayName(projectObjective.completedByUserId) && <span>·</span>}
                                  <span>{formatCompletionDate(projectObjective.completedAt)}</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Compact Actions */}
                    {canEditObjectives && (
                      <div className="relative flex-shrink-0" ref={el => menuRefs.current[objectiveKey] = el}>
                        <button
                          type="button"
                          ref={el => menuButtonRefs.current[objectiveKey] = el}
                          onClick={() => showMenu ? setMenuOpenId(null) : openMenu(objectiveKey)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                          aria-label="Options"
                        >
                          <MoreVertical className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
                        </button>
                        
                        {showMenu && createPortal(
                          <div 
                            id="objectives-menu-portal"
                            className="fixed z-[60000] w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden py-1"
                            style={{ top: menuPosition.top, right: menuPosition.right }}
                          >
                            {shouldShowCompletion && (
                              <button
                                onClick={() => handleToggleCompletion(objectiveKey, !isCompleted)}
                                disabled={isLocalMode ? false : toggleCompletionMutation.isPending}
                                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors disabled:opacity-50 ${
                                  isCompleted
                                    ? 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                    : 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                                }`}
                              >
                                {isCompleted ? <RotateCcw className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                                {isCompleted ? 'Undo' : 'Complete'}
                              </button>
                            )}
                            {sortedObjectives.length > 1 && (
                              <>
                                <button
                                  onClick={() => { moveObjective(index, 'up'); setMenuOpenId(null); }}
                                  disabled={index === 0}
                                  className="w-full text-left px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2 disabled:opacity-30"
                                >
                                  <ChevronUp className="w-3 h-3" /> Up
                                </button>
                                <button
                                  onClick={() => { moveObjective(index, 'down'); setMenuOpenId(null); }}
                                  disabled={index === sortedObjectives.length - 1}
                                  className="w-full text-left px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2 disabled:opacity-30"
                                >
                                  <ChevronDown className="w-3 h-3" /> Down
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => startEdit(projectObjective)}
                              className="w-full text-left px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center gap-2"
                            >
                              <Edit2 className="w-3 h-3" /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(objectiveKey, projectObjective.objectiveName)}
                              className="w-full text-left px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ObjectivesList;
